import { Component, Inject, OnInit } from '@angular/core';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { MatLegacySlideToggleChange as MatSlideToggleChange } from '@angular/material/legacy-slide-toggle';
import { Features } from '@app/core/user-permissions/user-permissions.model';
import { UserPermissionsService } from '@app/core/user-permissions/user-permissions.service';
import { BehaviorSubject } from 'rxjs';
import { finalize, switchMap, tap } from 'rxjs/operators';
import { Code, DeviceExtended } from '../../data/smart-devices.interface';
import { SmartDevicesService } from '../../service/smart-devices.service';
import { DialogCodeReservationDetailsComponent } from '../dialog-code-reservation-details/dialog-code-reservation-details.component';

@Component({
  standalone: false,
  selector: 'sbnb-dialog-device-details',
  templateUrl: './dialog-device-details.component.html',
  styleUrls: ['./dialog-device-details.component.scss'],
})
export class DialogDeviceDetailsComponent implements OnInit {
  changingLockStatus = false;
  changingThermostatSettings = false;

  showAccessCodes = false;
  loadingAccessCodes = false;

  accessCodesPage$ = new BehaviorSubject<number>(1);
  accessCodes$ = this.accessCodesPage$.pipe(
    tap(() => (this.loadingAccessCodes = true)),
    switchMap((page) => {
      return this.smartDevicesService.getAccessCodesForDevice(this.dialogData.device.id, page);
    }),
    tap(() => (this.loadingAccessCodes = false))
  );
  canLock: boolean = this.dialogData.device.actions.includes('lock') && this.dialogData.device.device_properties.online;

  hasWritePermission$ = this.permsService.hasAccessTo(Features.PERM_SMARTLOCKS_DASHBOARD_WRITE);

  DATE_FORMAT = "MMM d - h:mm aaaaa'm'";

  constructor(
    @Inject(MAT_DIALOG_DATA) public dialogData: { device: DeviceExtended },
    public dialogRef: MatDialogRef<DialogCodeReservationDetailsComponent>,
    private smartDevicesService: SmartDevicesService,
    private permsService: UserPermissionsService
  ) {}

  ngOnInit(): void {}

  openCodeModal(code: Code) {
    this.dialogRef.close({ openCodeModal: true, reservationId: code.reservation.reservation_id });
  }

  onEditClicked() {
    this.dialogRef.close({ openEditModal: true, device: this.dialogData.device });
  }

  onDeviceLockToggled(event: MatSlideToggleChange) {
    this.changingLockStatus = true;

    if (event.checked) {
      this.smartDevicesService.lockDevice(this.dialogData.device.id).subscribe(
        () => {
          this.changingLockStatus = false;
          this.dialogData.device.device_properties.locked = true;
        },
        () => {
          this.changingLockStatus = false;
        }
      );
    } else {
      this.smartDevicesService.unlockDevice(this.dialogData.device.id).subscribe(
        () => {
          this.changingLockStatus = false;
          this.dialogData.device.device_properties.locked = false;
        },
        () => {
          this.changingLockStatus = false;
        }
      );
    }
  }

  public pageChanged(page: number) {
    this.accessCodesPage$.next(page);
  }

  onThermostatModeChange(mode: string) {
    if (this.changingThermostatSettings) return;

    this.changingThermostatSettings = true;
    const payload = {
      hvac_mode: mode.toLowerCase(),
      heating_point: this.dialogData.device.device_properties.climate_setting.heating_point.fahrenheit,
      cooling_point: this.dialogData.device.device_properties.climate_setting.cooling_point.fahrenheit,
      action: 'hvac_change',
    };

    this.smartDevicesService
      .changeThermostatSettings(this.dialogData.device.id, payload)
      .pipe(
        switchMap(() =>
          this.smartDevicesService.getDeviceDetails(this.dialogData.device.id, this.dialogData.device.device_type)
        ),
        tap((updatedDevice) => {
          this.dialogData.device = updatedDevice;
        }),
        finalize(() => {
          this.changingThermostatSettings = false;
        })
      )
      .subscribe();
  }

  onFanModeChange(mode: string) {
    if (this.changingThermostatSettings) return;

    this.changingThermostatSettings = true;
    const payload = {
      fan_mode: mode.toLowerCase(),
    };

    this.smartDevicesService
      .changeThermostatSettings(this.dialogData.device.id, payload)
      .pipe(
        tap(() => {
          this.dialogData.device.device_properties.climate_setting.fan_mode = mode;
        }),
        finalize(() => {
          this.changingThermostatSettings = false;
        })
      )
      .subscribe();
  }

  onTemperatureChange(type: 'heating_change' | 'cooling_change' = null) {
    let heatingPoint = this.dialogData.device.device_properties.climate_setting.heating_point.fahrenheit;
    let coolingPoint = this.dialogData.device.device_properties.climate_setting.cooling_point.fahrenheit;
    const mode = this.dialogData.device.device_properties.climate_setting.mode.toLowerCase();
    const supportedTemps = this.dialogData.device.device_properties.supported_temperatures;

    // In heat_cool mode, maintain minimum delta by adjusting the other temperature
    if (mode === 'heat_cool') {
      const delta = supportedTemps.min_heating_cooling_delta_fahrenheit;

      // If heating was changed, adjust cooling up if needed
      if (type === 'heating_change' && coolingPoint - heatingPoint < delta) {
        coolingPoint = heatingPoint + delta + 1; // for some reason the request is not going through if delta is exactly met
        this.dialogData.device.device_properties.climate_setting.cooling_point.fahrenheit = coolingPoint;
      }

      // If cooling was changed, adjust heating down if needed
      if (type === 'cooling_change' && coolingPoint - heatingPoint < delta) {
        heatingPoint = coolingPoint - delta - 1;
        this.dialogData.device.device_properties.climate_setting.heating_point.fahrenheit = heatingPoint;
      }
    }

    // Don't proceed if either temperature is below minimum
    if (
      heatingPoint < supportedTemps.min_heating_set_point_fahrenheit ||
      coolingPoint < supportedTemps.min_cooling_set_point_fahrenheit ||
      (mode === 'heat_cool' && coolingPoint - heatingPoint < supportedTemps.min_heating_cooling_delta_fahrenheit)
    ) {
      return;
    }

    // Ensure temperatures are within valid range
    heatingPoint = Math.min(
      supportedTemps.max_heating_set_point_fahrenheit - supportedTemps.min_heating_cooling_delta_fahrenheit,
      heatingPoint
    );
    coolingPoint = Math.max(
      heatingPoint + supportedTemps.min_heating_cooling_delta_fahrenheit,
      Math.min(supportedTemps.max_cooling_set_point_fahrenheit, coolingPoint)
    );

    // Update the UI values
    this.dialogData.device.device_properties.climate_setting.heating_point.fahrenheit = heatingPoint;
    this.dialogData.device.device_properties.climate_setting.cooling_point.fahrenheit = coolingPoint;

    // Only send request if both temperatures are valid
    if (this.isValidHeatingPoint(heatingPoint) && this.isValidCoolingPoint(coolingPoint)) {
      this.changingThermostatSettings = true;
      const payload = {
        heating_point: heatingPoint,
        cooling_point: coolingPoint,
        hvac_mode: mode,
        action: 'temperature_change',
      };

      this.smartDevicesService
        .changeThermostatSettings(this.dialogData.device.id, payload)
        .pipe(
          finalize(() => {
            this.changingThermostatSettings = false;
          })
        )
        .subscribe();
    }
  }

  private updateTemperature() {
    let heatingPoint = this.dialogData.device.device_properties.climate_setting.heating_point.fahrenheit;
    let coolingPoint = this.dialogData.device.device_properties.climate_setting.cooling_point.fahrenheit;
    const mode = this.dialogData.device.device_properties.climate_setting.mode.toLowerCase();
    const supportedTemps = this.dialogData.device.device_properties.supported_temperatures;

    // Ensure temperatures are within valid range
    heatingPoint = Math.max(
      supportedTemps.min_heating_set_point_fahrenheit,
      Math.min(
        supportedTemps.max_heating_set_point_fahrenheit - supportedTemps.min_heating_cooling_delta_fahrenheit,
        heatingPoint
      )
    );
    coolingPoint = Math.max(
      heatingPoint + supportedTemps.min_heating_cooling_delta_fahrenheit,
      Math.min(supportedTemps.max_cooling_set_point_fahrenheit, coolingPoint)
    );

    // Update the UI values
    this.dialogData.device.device_properties.climate_setting.heating_point.fahrenheit = heatingPoint;
    this.dialogData.device.device_properties.climate_setting.cooling_point.fahrenheit = coolingPoint;

    // Only send request if temperatures are within valid range
    if (this.isValidHeatingPoint(heatingPoint) && this.isValidCoolingPoint(coolingPoint)) {
      this.changingThermostatSettings = true;
      const payload = {
        heating_point: heatingPoint,
        cooling_point: coolingPoint,
        hvac_mode: mode,
        action: 'temperature_change',
      };

      this.smartDevicesService
        .changeThermostatSettings(this.dialogData.device.id, payload)
        .pipe(
          finalize(() => {
            this.changingThermostatSettings = false;
          })
        )
        .subscribe();
    }
  }

  isValidHeatingPoint(temp: number): boolean {
    const mode = this.dialogData.device.device_properties.climate_setting.mode.toLowerCase();
    const coolingPoint = this.dialogData.device.device_properties.climate_setting.cooling_point.fahrenheit;
    const supportedTemps = this.dialogData.device.device_properties.supported_temperatures;

    if (
      temp < supportedTemps.min_heating_set_point_fahrenheit ||
      temp > supportedTemps.max_heating_set_point_fahrenheit
    ) {
      return false;
    }

    if (mode === 'heat_cool' && coolingPoint - temp < supportedTemps.min_heating_cooling_delta_fahrenheit) {
      return false;
    }

    return true;
  }

  isValidCoolingPoint(temp: number): boolean {
    const mode = this.dialogData.device.device_properties.climate_setting.mode.toLowerCase();
    const heatingPoint = this.dialogData.device.device_properties.climate_setting.heating_point.fahrenheit;
    const supportedTemps = this.dialogData.device.device_properties.supported_temperatures;

    if (
      temp < supportedTemps.min_cooling_set_point_fahrenheit ||
      temp > supportedTemps.max_cooling_set_point_fahrenheit
    ) {
      return false;
    }

    if (mode === 'heat_cool' && temp - heatingPoint < supportedTemps.min_heating_cooling_delta_fahrenheit) {
      return false;
    }

    return true;
  }
}
